import React, { useState, useEffect } from 'react';
import {
  Box, Chip, Grid, MenuItem, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { AddCircleOutline, FactCheckOutlined, RemoveCircleOutline } from '@mui/icons-material';
import dayjs from 'dayjs';
import 'dayjs/locale/fr';
import Layout from '../components/Layout';
import { Kpi, Panel } from '../components/ui';
import { tokens } from '../theme';

const InventoryHistory = () => {
  const [adjustments, setAdjustments] = useState([]);
  const [allAdjustments, setAllAdjustments] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState('all');

  useEffect(() => {
    loadAdjustments();
  }, []);

  useEffect(() => {
    filterByMonth();
  }, [selectedMonth, allAdjustments]); // eslint-disable-line

  const loadAdjustments = async () => {
    try {
      const response = await fetch('/api/stock/inventaires', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      
      if (response.ok) {
        const data = await response.json();

        setAllAdjustments(data);
        setAdjustments(data);
      }
    } catch (error) {
      console.error('Erreur:', error);
    }
  };

  const filterByMonth = () => {
    if (selectedMonth === 'all') {
      setAdjustments(allAdjustments);
    } else {
      const filtered = allAdjustments.filter(adj => 
        dayjs(adj.created_at).format('YYYY-MM') === selectedMonth
      );
      setAdjustments(filtered);
    }
  };

  const getAvailableMonths = () => {
    const months = [...new Set(allAdjustments.map(adj => 
      dayjs(adj.created_at).format('YYYY-MM')
    ))].sort().reverse();
    return months;
  };

  const exc = adjustments.filter((a) => a.difference > 0).reduce((t, a) => t + Math.abs(a.difference), 0);
  const man = adjustments.filter((a) => a.difference < 0).reduce((t, a) => t + Math.abs(a.difference), 0);

  return (
    <Layout title="Historique des inventaires" plain
      actions={(
        <TextField select size="small" label="Mois" value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)} sx={{ minWidth: 190 }}>
          <MenuItem value="all">Tous les mois</MenuItem>
          {getAvailableMonths().map((m) => <MenuItem key={m} value={m}>{dayjs(m).locale('fr').format('MMMM YYYY')}</MenuItem>)}
        </TextField>
      )}>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={4}><Kpi icon={FactCheckOutlined} tint="navy" label="Ajustements" value={adjustments.length} sub={selectedMonth === 'all' ? 'Toutes périodes' : dayjs(selectedMonth).locale('fr').format('MMMM YYYY')} /></Grid>
        <Grid item xs={6} sm={4}><Kpi icon={AddCircleOutline} tint="green" label="Excédents trouvés" value={exc ? `+${exc}` : 0} sub="Physique > théorique" /></Grid>
        <Grid item xs={6} sm={4}><Kpi icon={RemoveCircleOutline} tint="red" label="Manquants" value={man ? `−${man}` : 0} sub="Physique < théorique" valueColor={man ? '#B4232A' : undefined} /></Grid>
      </Grid>

      <Panel flush>
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell><TableCell>Type PBA</TableCell><TableCell align="right">Théorique</TableCell>
                <TableCell align="right">Physique</TableCell><TableCell align="right">Écart</TableCell><TableCell>Raison</TableCell><TableCell>Par</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {adjustments.length === 0 ? (
                <TableRow><TableCell colSpan={7} align="center" sx={{ py: 5, color: 'text.secondary' }}>
                  Aucun ajustement d'inventaire. Les écarts constatés lors des inventaires physiques apparaîtront ici.
                </TableCell></TableRow>
              ) : adjustments.map((adj, i) => {
                const plus = adj.difference > 0;
                return (
                  <TableRow key={i} hover>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{dayjs(adj.created_at).format('DD/MM/YYYY HH:mm')}</TableCell>
                    <TableCell>
                      <Typography sx={{ fontWeight: 600, fontSize: 14 }}>{adj.pba_code}</Typography>
                      {adj.pba_description && <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>{adj.pba_description}</Typography>}
                    </TableCell>
                    <TableCell align="right" sx={{ fontFamily: tokens.mono }}>{adj.theoretical_quantity}</TableCell>
                    <TableCell align="right" sx={{ fontFamily: tokens.mono }}>{adj.physical_quantity}</TableCell>
                    <TableCell align="right">
                      <Chip size="small" variant="outlined" color={plus ? 'success' : 'error'}
                        label={`${plus ? '+' : '−'}${Math.abs(adj.difference)} ${plus ? 'excédent' : 'manquant'}`} sx={{ fontFamily: tokens.mono, fontWeight: 600 }} />
                    </TableCell>
                    <TableCell sx={{ color: 'text.secondary' }}>{adj.notes || 'Inventaire physique'}</TableCell>
                    <TableCell>{adj.created_by_username || '—'}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Panel>
    </Layout>
  );
};

export default InventoryHistory;